const WORKER = process.env.REACT_APP_WORKER_URL || 'https://contenuia-proxy.soihibaliousseni.workers.dev';
const TIMEOUT_MS = 45000;

/** Erreur métier lisible par l'utilisateur. */
export class AIServiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AIServiceError';
  }
}

/** fetch avec timeout — évite les requêtes qui pendent indéfiniment. */
async function fetchWithTimeout(url: string, options: RequestInit = {}, timeout = TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      throw new AIServiceError("La requête a pris trop de temps. Réessayez.");
    }
    throw new AIServiceError("Problème de connexion au serveur. Vérifiez votre réseau.");
  } finally {
    clearTimeout(id);
  }
}

// ===== APPEL GROQ =====
async function callGroq(system: string, user: string, maxTokens = 900): Promise<string> {
  try {
    const res = await fetchWithTimeout(WORKER + '/groq', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system, user, max_tokens: maxTokens }),
    });

    if (!res.ok) {
      if (res.status === 429) throw new AIServiceError("Limite de requêtes atteinte. Patientez une minute.");
      if (res.status >= 500) throw new AIServiceError("Le service IA est momentanément indisponible.");
      throw new AIServiceError("La génération a échoué (code " + res.status + ").");
    }

    const data = await res.json();
    const text = data.text || data.choices?.[0]?.message?.content || '';
    if (!text) throw new AIServiceError("L'IA n'a pas retourné de contenu. Réessayez.");
    return text;
  } catch (e: any) {
    if (e instanceof AIServiceError) throw e;
    console.error('[aiService] callGroq:', e);
    throw new AIServiceError("La génération a échoué. Réessayez dans un instant.");
  }
}

/** Appel TTS Google — retourne l'audio base64 ou throw une erreur lisible. */
export async function callTTS(text: string, languageCode: string, voiceName: string): Promise<string> {
  try {
    const res = await fetchWithTimeout(WORKER + '/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: text.substring(0, 800), languageCode, voiceName }),
    });
    if (!res.ok) throw new AIServiceError("La génération de la voix a échoué.");
    const data = await res.json();
    if (!data.audioContent) throw new AIServiceError("Aucun audio généré. Vérifiez le texte du script.");
    return data.audioContent;
  } catch (e: any) {
    if (e instanceof AIServiceError) throw e;
    console.error('[aiService] callTTS:', e);
    throw new AIServiceError("La voix IA est indisponible. Réessayez.");
  }
}

/** Recherche une vidéo Pexels. Retourne null en cas d'échec (non bloquant). */
export async function fetchPexelsVideo(query: string): Promise<{ url: string; thumbnail: string; author: string; duration: number } | null> {
  try {
    const page = Math.floor(Math.random() * 8) + 1;
    const res = await fetchWithTimeout(WORKER + '/pexels-video?q=' + encodeURIComponent(query) + '&page=' + page, {}, 20000);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.url) return null;
    return { url: data.url, thumbnail: data.thumbnail || '', author: data.author || '', duration: data.duration || 0 };
  } catch (e) {
    console.warn('[aiService] fetchPexelsVideo:', e);
    return null;
  }
}

function parseJSON(text: string): any {
  try {
    return JSON.parse(text.replace(/```json|```/g, '').trim());
  } catch {
    return null;
  }
}

// ===== PERSONAS =====
const PERSONAS: Record<string, { nom: string; emoji: string; style: string; genre?: string }> = {
  hormozi: { nom: 'Alex Hormozi', emoji: '💰', style: 'Tu es Alex Hormozi. Style direct, valeur massive, zero blabla. Chaque phrase doit valoir de lor.' },
  welsh: { nom: 'Yomi Welsh', emoji: '🎯', style: 'Tu es Yomi Welsh. Style conversationnel francais, storytelling, exemples concrets du quotidien.' },
  moubeche: { nom: 'Thibault Moubeche', emoji: '🚀', style: 'Tu es un expert SaaS francophone. Vocabulaire startup, metrics, growth hacking, B2B.' },
  garyvee: { nom: 'Gary Vee', emoji: '⚡', style: 'Tu es Gary Vaynerchuk. Energie brute, motivation, hustle, verites qui font mal mais qui font grandir.' },
  mrbeast: { nom: 'MrBeast', emoji: '🎬', style: 'Tu es un expert YouTube. Hooks impossibles a ignorer, curiosite, promesse forte des la premiere seconde.' },
};

const MOTS_INTERDITS = `MOTS STRICTEMENT INTERDITS : "Découvrez", "Révolutionnez", "Boostez", "Transformez", "Solution innovante", "N'hésitez pas", "Dans un monde", "À l'ère du numérique", "Passionné par", "Il est crucial", "Innovant", "Robuste", "Pionnier", "Synergies", "Valeur ajoutée", "Cordialement".`;

// ===== VOIX IA =====
export const VOIX_IA: Record<string, { label: string; languageCode: string; voiceName: string; emoji: string; genre: string; langue: string }> = {
  'fr-femme-1': { label: 'Sophie', languageCode: 'fr-FR', voiceName: 'fr-FR-Neural2-A', emoji: '👩', genre: 'Femme', langue: 'FR' },
  'fr-femme-2': { label: 'Marie', languageCode: 'fr-FR', voiceName: 'fr-FR-Neural2-C', emoji: '👩‍💼', genre: 'Femme', langue: 'FR' },
  'fr-femme-3': { label: 'Léa', languageCode: 'fr-FR', voiceName: 'fr-FR-Neural2-E', emoji: '👧', genre: 'Femme', langue: 'FR' },
  'fr-homme-1': { label: 'Thomas', languageCode: 'fr-FR', voiceName: 'fr-FR-Neural2-B', emoji: '👨', genre: 'Homme', langue: 'FR' },
  'fr-homme-2': { label: 'Lucas', languageCode: 'fr-FR', voiceName: 'fr-FR-Neural2-D', emoji: '👨‍💼', genre: 'Homme', langue: 'FR' },
  'en-femme-1': { label: 'Emma', languageCode: 'en-US', voiceName: 'en-US-Neural2-C', emoji: '🇺🇸', genre: 'Femme', langue: 'EN' },
  'en-femme-2': { label: 'Olivia', languageCode: 'en-US', voiceName: 'en-US-Neural2-E', emoji: '🇬🇧', genre: 'Femme', langue: 'EN' },
  'en-homme-1': { label: 'James', languageCode: 'en-US', voiceName: 'en-US-Neural2-A', emoji: '🇺🇸', genre: 'Homme', langue: 'EN' },
  'en-homme-2': { label: 'William', languageCode: 'en-US', voiceName: 'en-US-Neural2-D', emoji: '🇬🇧', genre: 'Homme', langue: 'EN' },
  'es-femme': { label: 'Isabella', languageCode: 'es-ES', voiceName: 'es-ES-Neural2-A', emoji: '🇪🇸', genre: 'Femme', langue: 'ES' },
  'es-homme': { label: 'Carlos', languageCode: 'es-ES', voiceName: 'es-ES-Neural2-B', emoji: '🇪🇸', genre: 'Homme', langue: 'ES' },
  'pt-femme': { label: 'Ana', languageCode: 'pt-BR', voiceName: 'pt-BR-Neural2-A', emoji: '🇧🇷', genre: 'Femme', langue: 'PT' },
  'ar-homme': { label: 'Ahmed', languageCode: 'ar-XA', voiceName: 'ar-XA-Neural2-A', emoji: '🇸🇦', genre: 'Homme', langue: 'AR' },
  'de-femme': { label: 'Anna', languageCode: 'de-DE', voiceName: 'de-DE-Neural2-A', emoji: '🇩🇪', genre: 'Femme', langue: 'DE' },
  'it-homme': { label: 'Marco', languageCode: 'it-IT', voiceName: 'it-IT-Neural2-A', emoji: '🇮🇹', genre: 'Homme', langue: 'IT' },
};

// ===== VIDEO STYLES =====
export const VIDEO_STYLES: Record<string, { label: string; icon: string; color: string }> = {
  tiktok: { label: 'TikTok', icon: '🎵', color: '#FF0050' },
  youtube: { label: 'YouTube Shorts', icon: '🔴', color: '#FF0000' },
  linkedin: { label: 'LinkedIn Video', icon: '💼', color: '#0077B5' },
  pub_saas: { label: 'Pub SaaS', icon: '💎', color: '#6C47FF' },
  formation: { label: 'Formation', icon: '🎓', color: '#00C48C' },
};

// ===== HOOK TYPES & PLATFORMS =====
export const HOOK_TYPES = [
  { id: 'choc', label: 'Statistique choc', emoji: '💥' },
  { id: 'curiosite', label: 'Curiosité', emoji: '🤔' },
  { id: 'business', label: 'Business direct', emoji: '💼' },
  { id: 'vente', label: 'Vente urgente', emoji: '🔥' },
  { id: 'story', label: 'Storytelling', emoji: '📖' },
];

export const HOOK_PLATFORMS = [
  { id: 'tiktok', label: 'TikTok', emoji: '🎵' },
  { id: 'youtube', label: 'YouTube', emoji: '🔴' },
  { id: 'linkedin', label: 'LinkedIn', emoji: '💼' },
  { id: 'instagram', label: 'Instagram', emoji: '📸' },
  { id: 'facebook', label: 'Facebook', emoji: '📘' },
];

// ===== TONS DE CONTENU (V61 — variation automatique) =====
export const TONS_CONTENU = [
  'professionnel', 'pédagogique', 'inspirant', 'urgent', 'humoristique',
  'étude de cas', 'témoignage', 'tutoriel', 'comparaison', 'provocateur',
];

// ===== GENERATE CONTENT =====
export async function generateProductContent(params: {
  produit: string; cible: string; prix: string; objectif: string;
  description: string; plateforme: string; ton: string; langue: string;
}): Promise<Record<string, string>> {
  const system = `Tu es expert en marketing de contenu B2B francophone. ${MOTS_INTERDITS} Réponds UNIQUEMENT en JSON valide sans markdown.`;
  const user = `Produit: ${params.produit}\nCible: ${params.cible}\nPrix: ${params.prix || 'non précisé'}\nObjectif: ${params.objectif}\nDescription: ${params.description || ''}\nPlateforme: ${params.plateforme}\nTon: ${params.ton}\nLangue: ${params.langue}\n\nJSON: {"post_linkedin":"Post LinkedIn 300-500 mots avec emojis, hook, CTA","hook_viral":"5 hooks numérotés","script_video":"Script 60s HOOK/DEV/CTA","email_prospect":"Email B2B objet+corps"}`;
  const text = await callGroq(system, user, 1500);
  const parsed = parseJSON(text);
  if (parsed) return parsed;
  // Fallback : l'IA a répondu mais pas en JSON — on récupère quand même le texte
  return { post_linkedin: text.substring(0, 800), hook_viral: '', script_video: '', email_prospect: '' };
}

// ===== GENERATE HOOKS V2 =====
export async function generateHooks(params: {
  produit: string; cible: string; type: string; plateforme: string; langue: string;
}): Promise<string[]> {
  const system = `Tu es expert en hooks viraux pour ${params.plateforme}. ${MOTS_INTERDITS} Réponds UNIQUEMENT en JSON.`;
  const user = `10 hooks "${params.type}" pour ${params.produit} ciblant ${params.cible} en ${params.langue}.\nJSON: {"hooks":["hook1","hook2",...,"hook10"]}`;
  const text = await callGroq(system, user, 800);
  const hooks = parseJSON(text)?.hooks;
  if (Array.isArray(hooks) && hooks.length) return hooks;
  // Fallback : découper le texte brut en lignes
  return text.split('\n').map(l => l.replace(/^[\d.\-)\s]+/, '').trim()).filter(l => l.length > 10).slice(0, 10);
}

// ===== GENERATE SCRIPT (V61 Storytelling) =====
export async function generateScript(params: {
  produit: string; duree: string; format: string; langue: string; message: string; persona?: string;
}): Promise<string> {
  const persona = PERSONAS[params.persona || 'mrbeast'] || PERSONAS['mrbeast'];
  const system = `${persona.style}\n\n${MOTS_INTERDITS}\n\nSTRUCTURE OBLIGATOIRE (Storytelling marketing) :\n1. HOOK (0-3s) : Phrase choc qui arrête le scroll. MAX 8 mots.\n2. PROBLÈME (3-8s) : Douleur spécifique et émotionnelle de la cible.\n3. SOLUTION (8-15s) : Le produit = LA réponse. 1-2 phrases.\n4. PREUVE (15-22s) : Résultat concret, témoignage, chiffre réel.\n5. CTA (dernières 3-5s) : Appel à l'action fort et direct.\n\nZERO placeholder [Exemple] [Image] — texte prêt à lire face caméra.`;

  const titreAngle = params.message || '';
  const user = `TITRE: "${titreAngle || params.produit}"\nÉcris un script ${params.duree}s pour ${params.format} en ${params.langue}.\nLe hook DOIT reformuler directement ce titre. Max 80 mots.`;
  const script = await callGroq(system, user, 900);
  if (!script || script.trim().length < 20) {
    throw new AIServiceError("Le script généré est trop court. Précisez le titre et réessayez.");
  }
  return script;
}

// ===== GENERATE CALENDAR =====
export async function generateCalendar(params: {
  produit: string; secteur: string; langue: string; frequence: string;
}): Promise<any[]> {
  const system = `Tu es expert en stratégie de contenu. Réponds UNIQUEMENT en JSON valide.`;
  const user = `Calendrier 30 jours. Produit: ${params.produit} | Secteur: ${params.secteur} | Langue: ${params.langue}. Alterner: promo, valeur, conseils, tutoriels, témoignages, FAQ, coulisses, études de cas, CTA.\nJSON: {"days":[{"day":1,"theme":"theme","type":"Post","titre":"titre"},... 30 éléments]}`;
  const text = await callGroq(system, user, 2000);
  const days = parseJSON(text)?.days;
  return Array.isArray(days) ? days : [];
}

// ===== GENERATE YOUTUBE METADATA =====
export async function generateYouTubeMetadata(params: {
  produit: string; cible: string; script: string; langue: string;
}): Promise<{ titre: string; description: string; tags: string[] }> {
  const system = 'Expert YouTube SEO. Réponds UNIQUEMENT en JSON valide.';
  const user = `Produit: ${params.produit}\nCible: ${params.cible}\nScript: ${params.script.substring(0, 300)}\nLangue: ${params.langue}\n\nJSON: {"titre":"titre accrocheur max 60 chars","description":"description YouTube 200 mots avec liens et CTA","tags":["tag1","tag2","tag3","tag4","tag5"]}`;
  const text = await callGroq(system, user, 600);
  return parseJSON(text) || { titre: params.produit, description: '', tags: [params.produit, 'IA', 'Shorts'] };
}

// ===== SCORE VIRAL =====
export function calculerScoreViral(script: string): { score: number; details: Record<string, number> } {
  const hook = script.split(/[.!?]/)[0]?.length || 0;
  const hookScore = hook < 40 ? 25 : hook < 60 ? 20 : 10;
  const hasCTA = /essaie|clique|lien|gratuit|teste|découvre/i.test(script) ? 25 : 10;
  const rhythm = (script.match(/[.!?]/g) || []).length;
  const rhythmScore = rhythm > 5 ? 25 : rhythm > 3 ? 20 : 10;
  const length = script.length;
  const lengthScore = length > 100 && length < 500 ? 25 : 15;
  return { score: hookScore + hasCTA + rhythmScore + lengthScore, details: { hook: hookScore, cta: hasCTA, rhythm: rhythmScore, length: lengthScore } };
}

// ===== AGENT IA RÉALISATEUR =====
export async function agentRealisateur(params: {
  titre: string; produit: string; duree: string; langue: string;
}): Promise<{ script: string; score: number; scenes: string[]; suggestions: string[] }> {
  const script = await generateScript({ produit: params.produit, duree: params.duree, format: 'YouTube Shorts', langue: params.langue, message: params.titre, persona: 'mrbeast' });
  const { score } = calculerScoreViral(script);
  const scenes = script.split(/[.!?]/).filter(s => s.trim().length > 5).slice(0, 5).map(s => s.trim());
  return { script, score, scenes, suggestions: ['Raccourcir le hook à 5 mots max', 'Ajouter un chiffre concret', 'CTA plus direct'] };
}

// ===== 10 VARIANTES =====
export interface Variante { persona: string; emoji: string; script: string; hook: string; score: number; }

export async function generer10Variantes(params: {
  produit: string; titre: string; duree: string; langue: string;
}): Promise<Variante[]> {
  const variantes: Variante[] = [];
  const personasList = Object.entries(PERSONAS);

  // Chaque variante est indépendante : un échec ne bloque pas les autres
  for (const [, persona] of personasList) {
    try {
      const system = `${persona.style}\n${MOTS_INTERDITS}\nScript court unique. ZERO crochet.`;
      const user = `Script ${params.duree}s pour "${params.titre}" en ${params.langue}. Produit: ${params.produit}. Style ${persona.nom}. Max 80 mots.`;
      const script = await callGroq(system, user, 200);
      if (script?.trim()) {
        variantes.push({ persona: persona.nom, emoji: persona.emoji, script, hook: script.split(/[.!?]/)[0]?.trim() || '', score: Math.floor(Math.random() * 20) + 75 });
      }
    } catch (e) {
      console.warn('[aiService] Variante', persona.nom, 'échouée:', e);
    }
  }

  const angles = ['Choc statistique', 'Storytelling', 'Question provocante', 'Contre-intuitif', 'Avant/Après'];
  for (const angle of angles) {
    try {
      const script = await callGroq(`Expert marketing viral. Style: ${angle}. ZERO crochet.`, `Script ${params.duree}s angle "${angle}" pour "${params.titre}" en ${params.langue}. Max 80 mots.`, 200);
      if (script?.trim()) {
        variantes.push({ persona: angle, emoji: '✨', script, hook: script.split(/[.!?]/)[0]?.trim() || '', score: Math.floor(Math.random() * 15) + 80 });
      }
    } catch (e) {
      console.warn('[aiService] Variante', angle, 'échouée:', e);
    }
  }

  if (!variantes.length) {
    throw new AIServiceError("Aucune variante n'a pu être générée. Vérifiez votre connexion et réessayez.");
  }
  return variantes.sort((a, b) => b.score - a.score);
}

// ===== MINIATURE YOUTUBE IA =====
export async function genererMiniature(params: {
  titre: string; produit: string; score: number; brandingNom?: string; brandingCouleur?: string;
}): Promise<string> {
  try {
  const nom = params.brandingNom || params.produit || 'ContenuIA';
  const couleur = params.brandingCouleur || '#6C47FF';
  const canvas = document.createElement('canvas');
  canvas.width = 1280; canvas.height = 720;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createLinearGradient(0, 0, 1280, 720);
  grad.addColorStop(0, '#050510'); grad.addColorStop(0.6, '#0A0820'); grad.addColorStop(1, '#050515');
  ctx.fillStyle = grad; ctx.fillRect(0, 0, 1280, 720);
  ctx.strokeStyle = 'rgba(255,255,255,0.03)'; ctx.lineWidth = 1;
  for (let x = 0; x < 1280; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, 720); ctx.stroke(); }
  for (let y = 0; y < 720; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(1280, y); ctx.stroke(); }
  for (let i = 0; i < 4; i++) { ctx.strokeStyle = couleur + (i === 0 ? '40' : '15'); ctx.lineWidth = i === 0 ? 3 : 1.5; ctx.beginPath(); ctx.arc(1080, 360, 120 + i * 90, 0, Math.PI * 2); ctx.stroke(); }
  ctx.fillStyle = couleur; ctx.fillRect(0, 0, 6, 720);
  const scoreC = params.score >= 85 ? '#00F5A0' : '#FFB300';
  ctx.fillStyle = scoreC + 'CC'; ctx.beginPath(); ctx.roundRect(60, 55, 160, 58, 29); ctx.fill();
  ctx.fillStyle = '#000'; ctx.font = 'bold 30px Arial'; ctx.textAlign = 'center'; ctx.fillText(params.score + '/100', 140, 93);
  ctx.fillStyle = '#FFF'; ctx.font = 'bold 68px Arial'; ctx.textAlign = 'left';
  const words = params.titre.split(' '); const lines: string[] = []; let cur = '';
  for (const w of words) { const t = cur ? cur + ' ' + w : w; if (ctx.measureText(t).width > 960) { if (cur) lines.push(cur); cur = w; } else cur = t; }
  if (cur) lines.push(cur);
  lines.slice(0, 3).forEach((line, i) => { ctx.fillStyle = '#FFF'; ctx.fillText(line, 60, 210 + i * 88); });
  ctx.fillStyle = couleur + 'EE'; ctx.beginPath(); ctx.roundRect(60, 620, nom.length * 16 + 60, 58, 29); ctx.fill();
  ctx.fillStyle = '#FFF'; ctx.font = 'bold 26px Arial'; ctx.textAlign = 'center'; ctx.fillText(nom + ' ●', 60 + (nom.length * 16 + 60) / 2, 657);
  return canvas.toDataURL('image/jpeg', 0.92);
  } catch (e) {
    console.warn('[aiService] genererMiniature:', e);
    return '';
  }
}

// ===== OPTIMISEUR TITRE =====
export interface TitreOptimise { original: string; optimise: string; scoreAvant: number; scoreApres: number; ameliorations: string[]; }
export async function optimiserTitre(params: { titre: string; produit: string; plateforme: string; langue: string; }): Promise<TitreOptimise> {
  const system = 'Expert titres viraux. Réponds UNIQUEMENT en JSON valide.';
  const user = `Titre: "${params.titre}" | Produit: ${params.produit} | Plateforme: ${params.plateforme} | Langue: ${params.langue}\nRéécris pour score 90+. Max 60 chars.\nJSON: {"optimise":"nouveau titre","scoreAvant":52,"scoreApres":93,"ameliorations":["ajout chiffre","question directe"]}`;
  const text = await callGroq(system, user, 300);
  const p = parseJSON(text);
  return { original: params.titre, optimise: p?.optimise || params.titre, scoreAvant: p?.scoreAvant || 50, scoreApres: p?.scoreApres || 85, ameliorations: p?.ameliorations || [] };
}

// ===== SCORER PAR PLATEFORME =====
export interface ScorePlateforme { plateforme: string; icon: string; score: number; pointsForts: string[]; pointsFaibles: string[]; suggestion: string; }
export async function scorerParPlateforme(params: { titre: string; produit: string; langue: string; }): Promise<ScorePlateforme[]> {
  const system = 'Analyste contenu viral multi-plateformes. Réponds UNIQUEMENT en JSON valide.';
  const user = `Titre: "${params.titre}" | Produit: ${params.produit}\nAnalyse pour TikTok, YouTube Shorts, LinkedIn, Formation.\nJSON: {"scores":[{"plateforme":"TikTok","score":52,"pointsForts":["..."],"pointsFaibles":["..."],"suggestion":"..."},...]}`;
  const text = await callGroq(system, user, 1200);
  const p = parseJSON(text);
  const icons: Record<string, string> = { 'TikTok': '🎵', 'YouTube Shorts': '🔴', 'LinkedIn': '💼', 'Formation': '🎓' };
  return (p?.scores || []).map((s: any) => ({ ...s, icon: icons[s.plateforme] || '📱' }));
}

// ===== IA COACH =====
export interface CoachAnalyse { scoreGlobal: number; hookAnalyse: string; meilleureHeure: string; ctaAnalyse: string; dureeIdeale: string; conseils: string[]; prochainSujet: string; }
export async function coachAnalyse(params: { videos: { titre: string; vues: number }[]; produit: string; langue: string; }): Promise<CoachAnalyse> {
  const videosDesc = params.videos.map(v => '"' + v.titre + '" (' + v.vues + ' vues)').join(', ');
  const system = 'Coach expert YouTube/TikTok. Réponds UNIQUEMENT en JSON valide.';
  const user = `Vidéos: ${videosDesc}\nProduit: ${params.produit}\nJSON: {"scoreGlobal":72,"hookAnalyse":"...","meilleureHeure":"18h-20h","ctaAnalyse":"...","dureeIdeale":"25-35s","conseils":["...","...","...","..."],"prochainSujet":"..."}`;
  const text = await callGroq(system, user, 1000);
  const p = parseJSON(text);
  return { scoreGlobal: p?.scoreGlobal || 60, hookAnalyse: p?.hookAnalyse || '', meilleureHeure: p?.meilleureHeure || '18h-20h', ctaAnalyse: p?.ctaAnalyse || '', dureeIdeale: p?.dureeIdeale || '30s', conseils: p?.conseils || [], prochainSujet: p?.prochainSujet || '' };
}

// ===== GÉNÉRATION MULTI-FORMAT (V61) =====
export async function genererMultiFormat(params: {
  produit: string; description: string; cible: string; cta: string; hashtags: string[]; langue: string; ton: string;
}): Promise<Record<string, string>> {
  const hashStr = params.hashtags.map(h => '#' + h).join(' ');
  const system = `Expert contenu multi-plateforme. Adapte le format à chaque réseau. Ton: ${params.ton}. ${MOTS_INTERDITS} Réponds UNIQUEMENT en JSON valide.`;
  const user = `Produit: ${params.produit}\nDescription: ${params.description}\nCible: ${params.cible}\nCTA: ${params.cta}\nHashtags: ${hashStr}\nLangue: ${params.langue}\n\nGénère 7 versions adaptées:\nJSON: {"linkedin":"post pro 300 mots","facebook":"post engageant 200 mots","instagram":"caption + emojis 150 mots","tiktok":"script 30s","youtube":"script shorts 30s","x":"tweet 280 chars","threads":"post threads 200 mots"}`;
  const text = await callGroq(system, user, 2000);
  return parseJSON(text) || {};
}

// ===== ICP CONTENT =====
export async function generateICPContent(params: { produit: string; cible: string; langue: string; }): Promise<Record<string, string>> {
  const system = 'Expert ICP et marketing B2B. Réponds UNIQUEMENT en JSON valide.';
  const user = `Produit: ${params.produit}\nCible: ${params.cible}\nLangue: ${params.langue}\nJSON: {"icp":"description client idéal","douleurs":"3 douleurs","objections":"3 objections et réponses","declencheurs":"3 déclencheurs d achat","angle_contenu":"angle recommandé","cta_optimal":"CTA optimal"}`;
  const text = await callGroq(system, user, 1000);
  return parseJSON(text) || {};
}

// ===== VIRAL ENGINE — 30 idées =====
export async function getTrends(params: { produit: string; langue: string; }): Promise<any[]> {
  const system = 'Expert tendances virales. Réponds UNIQUEMENT en JSON valide.';
  const user = `30 idées de vidéos virales pour ${params.produit} en ${params.langue}. Variées: tutoriel, coulisses, FAQ, comparaison, storytelling, choc, challenge.\nJSON: {"ideas":[{"titre":"titre","type":"Tutoriel","score":85,"plateforme":"TikTok"},... 30 éléments]}`;
  const text = await callGroq(system, user, 2000);
  const ideas = parseJSON(text)?.ideas;
  return Array.isArray(ideas) ? ideas : [];
}
